import React, { useState, useEffect } from 'react';
import { Users, Mail, UserPlus, X, Clock, Trash2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface Workspace {
  id: string;
  name: string;
  color?: string;
  logo?: string;
  thumbnail?: string;
}

interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'client';
  status: 'active' | 'inactive';
  joined_at: string;
  updated_at: string;
  profile?: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  email: string;
  role: 'admin' | 'manager' | 'member';
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  invited_by: string;
  token: string;
  invited_at: string;
  expires_at: string;
  accepted_at: string | null;
  inviter_profile?: {
    first_name: string | null;
    last_name: string | null;
  };
}

interface MembersTabProps {
  workspace: Workspace;
  isGlobalDarkMode: boolean;
}

export const MembersTab: React.FC<MembersTabProps> = ({ workspace, isGlobalDarkMode: _ }) => {
  const { user, profile } = useAuth();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'manager' | 'member'>('member');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (workspace.id && user) {
      ensureUserIsMember();
      loadMembers();
      loadInvitations();
    }
  }, [workspace.id, user]);

  const ensureUserIsMember = async () => {
    if (!user) return;

    try {
      const { data: existingMember } = await supabase
        .from('workspace_members')
        .select('id')
        .eq('workspace_id', workspace.id)
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (!existingMember) {
        const { error: insertError } = await supabase
          .from('workspace_members')
          .insert({
            workspace_id: workspace.id,
            user_id: user.id,
            role: 'owner',
            status: 'active',
          });

        if (insertError) {
          console.error('Error adding user as workspace member:', insertError);
        }
      }
    } catch (err: any) {
      console.error('Error ensuring user is member:', err);
    }
  };

  const loadMembers = async () => {
    try {
      const { data: membersData, error: membersError } = await supabase
        .from('workspace_members')
        .select('*')
        .eq('workspace_id', workspace.id)
        .eq('status', 'active')
        .neq('role', 'client')
        .order('joined_at', { ascending: false });

      if (membersError) throw membersError;

      const membersWithProfiles = await Promise.all(
        (membersData || []).map(async (member) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('id, email, first_name, last_name, avatar_url')
            .eq('id', member.user_id)
            .single();

          return {
            ...member,
            profile: profileData || undefined,
          };
        })
      );

      setMembers(membersWithProfiles);
    } catch (err: any) {
      console.error('Error loading members:', err);
      setError('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('workspace_invitations')
        .select('*')
        .eq('workspace_id', workspace.id)
        .in('status', ['pending', 'accepted'])
        .order('invited_at', { ascending: false });

      if (invitationsError) throw invitationsError;

      const invitationsWithProfiles = await Promise.all(
        (invitationsData || []).map(async (invitation) => {
          const { data: inviterData } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', invitation.invited_by)
            .maybeSingle();

          return {
            ...invitation,
            inviter_profile: inviterData || undefined,
          };
        })
      );

      setInvitations(invitationsWithProfiles);
    } catch (err: unknown) {
      console.error('Error loading invitations:', err);
    }
  };

  const sendInvitationEmail = async (email: string, token: string) => {
    try {
      const siteUrl = window.location.origin;
      const invitationLink = `${siteUrl}/accept-invitation?token=${token}&workspace=${workspace.id}`;

      const inviterName = profile?.first_name && profile?.last_name
        ? `${profile.first_name} ${profile.last_name}`.trim()
        : profile?.first_name || null;

      const { data: functionData, error: functionError } = await supabase.functions.invoke('send-invitation-email', {
        body: {
          email: email.toLowerCase().trim(),
          invitationLink,
          workspaceName: workspace.name,
          inviterName: inviterName || undefined,
          role: inviteRole,
        },
      });

      if (functionError) {
        console.error('Error calling email function:', functionError);
        const { data: existingUser } = await supabase
          .from('profiles')
          .select('id, email')
          .eq('email', email.toLowerCase().trim())
          .maybeSingle();

        if (existingUser) {
          const { error: emailError } = await supabase.auth.signInWithOtp({
            email: email.toLowerCase().trim(),
            options: {
              emailRedirectTo: invitationLink,
              data: {
                workspace_id: workspace.id,
                workspace_name: workspace.name,
                invitation_token: token,
              },
            },
          });

          if (emailError) {
            console.error('Error sending fallback email:', emailError);
          }
        } else {
          console.log('Invitation link for new user:', invitationLink);
        }
      } else {
        console.log('Invitation email sent successfully:', functionData);
      }
    } catch (err: unknown) {
      console.error('Error sending invitation email:', err);
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !user) {
      setError('Please enter a valid email address');
      return;
    }

    setInviting(true);
    setError(null);
    setSuccess(null);

    try {
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', inviteEmail.toLowerCase().trim())
        .maybeSingle();

      if (existingProfile) {
        const { data: existingMember } = await supabase
          .from('workspace_members')
          .select('id')
          .eq('workspace_id', workspace.id)
          .eq('user_id', existingProfile.id)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();

        if (existingMember) {
          setError('This user is already a member of this workspace');
          setInviting(false);
          return;
        }
      }

      const { data: existingInvitation } = await supabase
        .from('workspace_invitations')
        .select('id')
        .eq('workspace_id', workspace.id)
        .eq('email', inviteEmail.toLowerCase().trim())
        .eq('status', 'pending')
        .maybeSingle();

      if (existingInvitation) {
        setError('An invitation has already been sent to this email');
        setInviting(false);
        return;
      }

      const { data: invitation, error: inviteError } = await supabase
        .from('workspace_invitations')
        .insert({
          workspace_id: workspace.id,
          email: inviteEmail.toLowerCase().trim(),
          role: inviteRole,
          invited_by: user.id,
        })
        .select()
        .single();

      if (inviteError) throw inviteError;

      if (invitation) {
        await sendInvitationEmail(invitation.email, invitation.token);
        setSuccess(`Invitation sent to ${inviteEmail}`);
        setInviteEmail('');
        await loadInvitations();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('Error inviting member:', err);
      setError(errorMessage || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('workspace_invitations')
        .update({ status: 'declined' })
        .eq('id', invitationId);

      if (error) throw error;
      await loadInvitations();
    } catch (err: any) {
      console.error('Error canceling invitation:', err);
      setError('Failed to cancel invitation');
    }
  };

  const getRoleLevel = (role: 'owner' | 'admin' | 'manager' | 'member' | 'client'): number => {
    const levels = { owner: 5, admin: 4, manager: 3, member: 2, client: 1 };
    return levels[role];
  };

  const getCurrentUserRole = (): 'owner' | 'admin' | 'manager' | 'member' | 'client' | null => {
    const currentUserMember = members.find(m => m.user_id === user?.id);
    return currentUserMember?.role || null;
  };

  const canRemoveMember = (member: WorkspaceMember): boolean => {
    if (!user) return false;
    if (member.role === 'owner') return false;
    const currentUserRole = getCurrentUserRole();
    if (!currentUserRole) return false;
    if (currentUserRole === 'owner' && member.user_id === user.id) return false;
    if (member.user_id === user.id) return true;
    const currentUserLevel = getRoleLevel(currentUserRole);
    const targetMemberLevel = getRoleLevel(member.role);
    return currentUserLevel > targetMemberLevel;
  };

  const handleRemoveMember = async (memberId: string) => {
    const memberToRemove = members.find(m => m.id === memberId);
    const isCurrentUser = memberToRemove?.user_id === user?.id;

    const confirmMessage = isCurrentUser
      ? 'Are you sure you want to leave this workspace?'
      : 'Are you sure you want to remove this member?';

    if (!confirm(confirmMessage)) return;

    try {
      const { error } = await supabase
        .from('workspace_members')
        .update({ status: 'inactive' })
        .eq('id', memberId);

      if (error) throw error;

      if (isCurrentUser) {
        window.location.href = '?modal=settings&tab=workspace';
      } else {
        await loadMembers();
        setSuccess('Member removed successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Error removing member:', JSON.stringify(err, null, 2));
      setError('Failed to remove member');
    }
  };

  const getInitials = (firstName: string | null, lastName: string | null, email: string | null | undefined) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (email && email.length > 0) {
      return email[0].toUpperCase();
    }
    return '?';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRoleBadgeClass = (role: 'owner' | 'admin' | 'manager' | 'member' | 'client') => {
    switch (role) {
      case 'owner': return 'bg-warning/15 text-warning';
      case 'admin': return 'bg-info/15 text-info';
      case 'manager': return 'bg-success/15 text-success';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  if (loading) {
    return null;
  }

  const currentUserRole = getCurrentUserRole();
  const canInviteMembers = currentUserRole && currentUserRole !== 'member';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2 text-foreground">Members</h2>
        <p className="text-sm text-muted-foreground">Manage workspace members</p>
      </div>

      {error && (
        <div className="rounded-lg border p-4 bg-destructive/10 border-destructive/30 text-destructive">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border p-4 bg-success/10 border-success/30 text-success">
          {success}
        </div>
      )}

      {canInviteMembers && (
        <div className="rounded-lg border p-6 bg-card border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-workspace-accent/15 text-workspace-accent">
              <UserPlus className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Invite Member</h3>
          </div>
          <form onSubmit={handleInviteMember} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="invite-email" className="text-foreground">
                  Email Address
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className="mt-1 bg-background border-input text-foreground placeholder:text-muted-foreground"
                  required
                />
              </div>
              <div>
                <Label htmlFor="invite-role" className="text-foreground">
                  Role
                </Label>
                <Select value={inviteRole} onValueChange={(value: 'admin' | 'manager' | 'member') => setInviteRole(value)}>
                  <SelectTrigger className="mt-1 bg-background border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-foreground">
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              type="submit"
              disabled={inviting}
              className="w-full md:w-auto bg-workspace-accent hover:bg-workspace-accent-hover text-white"
            >
              {inviting ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </form>
        </div>
      )}

      {/* Current Members */}
      <div className="rounded-lg border bg-card border-border">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">
              Current Members ({members.length})
            </h3>
          </div>
        </div>
        <div className="divide-y divide-border">
          {members.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No members yet. Invite someone to get started!</p>
            </div>
          ) : (
            members.map((member) => (
              <div
                key={member.id}
                className="p-4 flex items-center justify-between transition-colors hover:bg-accent/50"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={member.profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-muted text-muted-foreground">
                      {getInitials(
                        member.profile?.first_name || null,
                        member.profile?.last_name || null,
                        member.profile?.email || ''
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-foreground">
                      {member.profile?.first_name && member.profile?.last_name
                        ? `${member.profile.first_name} ${member.profile.last_name}`
                        : member.profile?.first_name || member.profile?.email || 'Unknown User'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {member.profile?.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(member.role)}`}>
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                  </span>
                  {canRemoveMember(member) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pending Invitations */}
      {canInviteMembers && invitations.filter(inv => inv.status === 'pending').length > 0 && (
        <div className="rounded-lg border bg-card border-border">
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground">
                Pending Invitations ({invitations.filter(inv => inv.status === 'pending').length})
              </h3>
            </div>
          </div>
          <div className="divide-y divide-border">
            {invitations
              .filter(inv => inv.status === 'pending')
              .map((invitation) => (
                <div
                  key={invitation.id}
                  className="p-4 flex items-center justify-between transition-colors hover:bg-accent/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-muted">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        {invitation.email}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Invited {formatDate(invitation.invited_at)} &bull; Expires {formatDate(invitation.expires_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-warning/15 text-warning">
                      <Clock className="w-3 h-3" />
                      Pending
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      invitation.role === 'admin'
                        ? 'bg-info/15 text-info'
                        : invitation.role === 'manager'
                        ? 'bg-success/15 text-success'
                        : 'bg-secondary text-secondary-foreground'
                    }`}>
                      {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
